export interface BlogPost {
  slug: string;
  title: string;
  description: string;
  date: string; // DD/MM/YYYY
  readMinutes: number;
  body: { heading?: string; text: string }[];
}

export const BLOG_POSTS: BlogPost[] = [
  {
    slug: "is-ai-feedback-allowed-at-university",
    title: "Is AI feedback allowed at UK universities? A student's guide",
    description:
      "What UK university AI policies usually allow, where the line sits between feedback and ghostwriting, and how to use AI critique without risking academic misconduct.",
    date: "13/10/2026",
    readMinutes: 7,
    body: [
      {
        text: "Almost every UK university now publishes guidance on generative AI, and most students reading it are left with one question: am I allowed to use AI at all? The short answer is that most policies do not ban AI outright. They draw a line between using AI to help you learn and using AI to produce work you then submit as your own.",
      },
      {
        heading: "What UK university policies usually say",
        text: "Policies differ in detail, but they tend to share three ideas. First, assessed work must be your own, so submitting AI-generated or AI-rewritten text as yours is normally treated as academic misconduct. Second, some uses are often permitted — brainstorming, checking understanding, or getting feedback on a draft — unless a module says otherwise. Third, you may be asked to declare any AI use, and individual modules can set stricter rules than the university-wide policy.",
      },
      {
        heading: "Feedback versus ghostwriting",
        text: "A useful test: who wrote the words? If a tool writes, rewrites or paraphrases your text, the words are no longer yours. If a tool reads your draft and tells you where the argument is thin or a reference is inconsistent, and you then make the changes, the words remain yours. That second kind of help is the same thing tutors, study-skills teams and peer reviewers have always offered.",
      },
      {
        heading: "Why this matters if you are worried about false accusations",
        text: "Many students are anxious about being wrongly accused of using AI. Keeping your drafts, notes and version history is the best protection you have: it shows how your work developed over time. A feedback tool that never edits your text supports that record rather than muddying it.",
      },
      {
        heading: "A quick checklist before you use any AI tool",
        text: "1. Read your university's AI guidance and your module handbook. 2. Check whether your assessment has its own AI rules. 3. Avoid any tool that writes, rewrites or paraphrases your work. 4. Keep your drafts and note what help you used. 5. Declare AI use if your university asks you to. 6. If you are unsure, ask your module leader before you submit.",
      },
      {
        heading: "Where Tiger's Eye fits",
        text: "Tiger's Eye is built around this line. It only critiques: it reads your draft against your brief, level of study and referencing style, and tells you what to look at. It never writes, rewrites or generates text for you, so your words — and your grade — stay yours. Always check your own university's policy first; rules vary.",
      },
    ],
  },
  {
    slug: "how-to-check-your-referencing-before-you-submit",
    title: "How to check your referencing before you submit: Harvard, OSCOLA and MHRA",
    description:
      "A practical pre-submission checklist for Harvard, OSCOLA and MHRA referencing — the common mistakes markers notice first and how to catch them.",
    date: "13/10/2026",
    readMinutes: 8,
    body: [
      {
        text: "Referencing errors are some of the easiest marks to lose and some of the easiest to fix. Markers notice them quickly, and inconsistent referencing can make strong work look careless. This guide gives you a checklist for any style, then the specific things to check in Harvard, OSCOLA and MHRA.",
      },
      {
        heading: "Step 1: confirm the exact style and version",
        text: "Check your module handbook. Many UK universities publish their own version of Harvard, and small differences — where the year goes, how page numbers are shown — matter. Always follow your university's guide rather than a generic website.",
      },
      {
        heading: "Step 2: the universal checklist",
        text: "Every in-text citation has a matching entry in your reference list or bibliography, and every entry is actually cited. Author names are spelled the same everywhere. Years match between citation and reference. Direct quotations have page numbers. Formatting — italics, capitals, punctuation — is consistent from the first entry to the last.",
      },
      {
        heading: "Harvard: what to check",
        text: "In-text citations give the author and year, for example (Smith, 2021), with a page number for quotations. The reference list is alphabetical by author surname. Check 'et al.' is used consistently for multiple authors, that the title in italics is the right part (the book or journal, not the article), and that online sources include the URL and access date if your university's version requires them.",
      },
      {
        heading: "OSCOLA: what to check",
        text: "OSCOLA, used in law, relies on footnotes rather than in-text citations. Check that every footnote ends with a full stop, that case names are italicised, and that neutral citations and law report references are in the correct order. Later references to the same source should use a short form, and your table of cases and legislation should be separate from your bibliography where required. OSCOLA uses minimal punctuation in abbreviations — for example, no full stops in report abbreviations.",
      },
      {
        heading: "MHRA: what to check",
        text: "MHRA, common in the humanities, also uses footnotes. The first citation of a source gives full details; later citations use a shortened form. Check that titles of books are italicised and article titles are in single quotation marks, that page ranges are given correctly, and that your bibliography lists surnames first while footnotes give names in their natural order.",
      },
      {
        heading: "Step 3: read it once, only for referencing",
        text: "Do a separate pass focused only on references, ideally on a printed copy or with the reference list open beside your draft. It is much easier to catch slips when you are not also reading for argument.",
      },
      {
        heading: "Get a second pair of eyes",
        text: "Tiger's Eye checks your draft against the referencing style you choose — Harvard, APA, OSCOLA, MHRA and more — and flags inconsistencies and missing references without changing a word of your work.",
      },
    ],
  },
  {
    slug: "what-markers-look-for",
    title: "What markers actually look for in an essay",
    description:
      "Argument, structure, evidence and clarity — how to read your own draft the way a marker will.",
    date: "13/10/2026",
    readMinutes: 4,
    body: [
      {
        text: "Marking criteria can feel abstract, but most come down to the same few questions. Reading your draft with them in mind helps you spot gaps before your marker does.",
      },
      {
        heading: "Does it answer the question?",
        text: "Re-read the brief. Every section should move your answer forward. If a paragraph does not, cut it or connect it.",
      },
      {
        heading: "Is the argument clear?",
        text: "Your marker should be able to summarise your position after reading the introduction. Signpost where you are going and remind the reader how each point supports it.",
      },
      {
        heading: "Is it backed by evidence?",
        text: "Claims need support from credible, well-referenced sources — and critical discussion of them, not just description.",
      },
      {
        heading: "Is it at the right level?",
        text: "Expectations rise from first year to master's level. Tiger's Eye tailors its critique to your level of study so the feedback matches what your markers expect.",
      },
    ],
  },
];

export function getPost(slug: string) {
  return BLOG_POSTS.find((p) => p.slug === slug);
}
